import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlus } from "react-icons/fa";
import { MdOutlineInventory2 } from "react-icons/md";
import {
  ArrowDown,
  ArrowDownRight,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Plus,
} from "lucide-react";
import Card from "components/MaterialCard";
import { BASE_URL } from "utils/constant";
import OrganizationEmptyState from "components/UI_components/OrganizationEmptyState";
import RequireAccess from "components/RequireAccess";
import LoadingScreen from "components/UI_components/LoadingScreen";
import Loader from "components/UI_components/Loader";
import VendorEmptyIcon from "assets/empty state/VendorEmpty.svg";
import NoItemIcon from "assets/empty state/NoItems.svg";
import CreateWarehouseMaterial from "../Procurement/CreateWarehouseMaterial";
import WarehouseMaterialSidebar from "../Procurement/WarehouseMaterialSidebar";
import SendReceiveSideBar from "../Procurement/SendReceiveSideBar";
import PurchaseOrder from "../Procurement/PurchaseOrder";
import { PiShoppingCartSimple } from "react-icons/pi";
import Button from "components/ui-custom/Button";
import { Tabs, TabsList, TabsTrigger } from "components/ui-custom/Tabs";

const WAREHOUSE_TABS = [
  {
    id: "warehouse-materials",
    label: "Warehouse Materials",
    icon: MdOutlineInventory2,
  },
  {
    id: "warehouse-purchases",
    label: "Warehouse Purchases",
    icon: PiShoppingCartSimple,
  },
];

const STATUS_STYLES = {
  closed: "text-[#D30606] bg-[#FFFFFF] border-1 rounded-full border-[#D30606]",
  cancelled:
    "text-[#D30606] bg-[#FFFFFF] border-1 rounded-full border-[#D30606]",
  canceled:
    "text-[#D30606] bg-[#FFFFFF] border-1 rounded-full border-[#D30606]",
  approved:
    "text-[#00C875] bg-[#FFFFFF] border-1 rounded-full border-[#ABEFC6]",
  completed:
    "text-[#0A66C2] bg-[#FFFFFF] border-1 rounded-full border-[#A3C9F5]",
  open: "text-[#F0B41D] bg-[#FFFFFF] border-1 rounded-full border-[#FEDF89]",
};

const formatPoDate = (iso) => {
  if (!iso) return "N/A";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const day = d.getDate();
  const month = d.toLocaleString("en-GB", { month: "short" });
  const year = String(d.getFullYear());
  return `${day} ${month} ${year}`;
};

const getMaterialMeta = (material) => {
  if (!material) return "";
  const specification =
    material?.specification || material?.material_spec || "";
  const subcategory = material?.subcategory || "";
  const brand = material?.brand_name || material?.brand || "";
  return [specification, subcategory, brand].filter(Boolean).join(" | ");
};

const getSortableTime = (item) => {
  const raw =
    item?.created_on ??
    item?.created_at ??
    item?.updated_on ??
    item?.updated_at ??
    item?.last_stock?.created_on ??
    item?.last_stock?.created_at;
  const parsed = raw ? new Date(raw).getTime() : NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const WarehousePurchaseRow = ({ po, onView }) => {
  const firstLine = po?.lines?.[0] || null;
  const firstLineMeta = getMaterialMeta(firstLine);
  const extraCount = Math.max((po?.lines?.length || 0) - 1, 0);
  const statusLower = (po.status || "").toLowerCase();
  const statusLabel =
    statusLower === "open"
      ? "Pending Approval"
      : statusLower === "closed" ||
          statusLower === "cancelled" ||
          statusLower === "canceled"
        ? "Cancelled"
        : po.status
          ? po.status.charAt(0).toUpperCase() + po.status.slice(1)
          : "Unknown";

  return (
    <div className="flex items-center justify-between py-2 px-4 border border-gray-200 rounded-lg mb-3 bg-white">
      <div className="w-[16%] text-[16px] font-medium text-[#232323]">
        {po.po_number}
        <div className="text-[14px] font-normal text-gray-500">
          {firstLine?.material_name || ""}
          {extraCount > 0 ? ` + ${extraCount} more` : ""}
        </div>
        {firstLineMeta && (
          <div className="text-[12px] text-gray-400 truncate">
            {firstLineMeta}
          </div>
        )}
      </div>

      <div className="w-[16%]">
        <p className="text-[16px] font-medium text-[#232323]">
          {po.rfq_number}
        </p>
      </div>

      <div className="w-[16%]">
        <p className="text-[14px] text-gray-500">Vendor</p>
        <p className="text-[16px] font-medium text-[#232323]">
          {po.vendor_name || "N/A"}
        </p>
      </div>

      <div className="w-[16%]">
        <p className="text-[14px] text-gray-500">Created on</p>
        <p className="text-[16px] font-medium text-[#232323]">
          {formatPoDate(po.created_on)}
        </p>
      </div>

      <div className="w-[16%] flex justify-end pr-4">
        <span
          className={`text-[12px] px-2 py-1 ${
            STATUS_STYLES[statusLower] || "text-gray-600 bg-gray-100"
          }`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="w-[16%] flex justify-end pr-3">
        <button
          onClick={() => onView(po)}
          className="px-4 py-1 text-sm border-1 rounded-md text-[#3B0069] border-[#3B0069] hover:bg-[#F5EEF9]"
        >
          View
        </button>
      </div>
    </div>
  );
};

const WarehouseMaterialRow = ({ item, onView, onSendReceiveClick }) => {
  const lastStock = item.last_stock;
  const material = item.material;
  const materialMeta = getMaterialMeta(material);

  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_220px] items-center gap-6 py-3 px-4 border border-gray-200 rounded-lg mb-3 bg-white"
      style={{ transition: "transform 0.2s" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.01)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <div className="min-w-0 cursor-pointer" onClick={onView}>
        <div className="text-[16px] font-medium text-[#232323]">
          {material.material_name}
        </div>
        {materialMeta && (
          <div className="text-[14px] text-gray-500">{materialMeta}</div>
        )}
      </div>

      <div className="min-w-0 cursor-pointer" onClick={onView}>
        <div className="text-[14px] text-gray-500">In stock</div>
        <div className="text-[16px] font-medium text-[#232323]">
          {item.current_quantity} {material.uom}
        </div>
      </div>

      <div className="min-w-0 cursor-pointer" onClick={onView}>
        <div className="flex items-center gap-1 text-[#3B0069] text-sm">
          <span>{lastStock?.site_name || "Warehouse"}</span>
          {lastStock?.status === "DEDUCTED" &&
            lastStock?.status_reason === "SENT_TO_SITE" && (
              <span className="flex items-center">
                <ArrowLeft size={16} />
              </span>
            )}
          {lastStock?.status === "ADDED" && (
            <span className="flex items-center">
              <ArrowRight size={16} />
            </span>
          )}
          <span className="bg-[#ECE6F0] h-[28px] w-[28px] rounded-full flex justify-center items-center">
            <MdOutlineInventory2 size={24} className="p-1" />
          </span>
        </div>
        <div>
          {lastStock?.status === "DEDUCTED" &&
            lastStock?.deducted_quantity > 0 && (
              <div className="flex justify-start items-center gap-1 text-sm text-red-600">
                <ArrowDownRight size={16} /> {lastStock?.deducted_quantity}{" "}
                {material.uom}
              </div>
            )}
          {lastStock?.status === "ADDED" && lastStock?.entered_quantity > 0 && (
            <div className="flex justify-start items-center gap-1 text-sm text-green-600">
              <ArrowUpRight size={16} /> {lastStock?.entered_quantity}{" "}
              {material.uom}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={onSendReceiveClick}
          className="px-3 py-1.5 text-sm border rounded-md text-[#3B0069] bg-[#ECE6F0] border-gray-200 hover:bg-gray-100"
        >
          Transfer Material
        </button>
      </div>
    </div>
  );
};

export default function Warehouse({ currentMember, permissionsLoaded }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Warehouse Materials");
  const [showCreateWarehouseMaterial, setShowCreateWarehouseMaterial] =
    useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showSendReceiveSideBar, setShowSendReceiveSideBar] = useState(false);
  const [openPo, setOpenPo] = useState(null);
  const [sortRecentDesc, setSortRecentDesc] = useState(true);

  const [materials, setMaterials] = useState(
    JSON.parse(localStorage.getItem("materials")) || [],
  );
  const [purchaseOrders, setPurchaseOrders] = useState(
    JSON.parse(localStorage.getItem("purchaseOrders")) || [],
  );
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [poLoading, setPoLoading] = useState(false);

  const organisationId = JSON.parse(localStorage.getItem("organization"))?.id;

  const pageLoading = warehouseLoading || poLoading;

  const sortedWarehouseMaterials = useMemo(() => {
    if (!materials || materials.length === 0) return materials;
    return [...materials].sort((a, b) =>
      sortRecentDesc
        ? getSortableTime(b) - getSortableTime(a)
        : getSortableTime(a) - getSortableTime(b),
    );
  }, [materials, sortRecentDesc]);

  const warehousePos = useMemo(() => {
    const base = purchaseOrders.filter((po) => !po.site_name);
    return [...base].sort((a, b) =>
      sortRecentDesc
        ? getSortableTime(b) - getSortableTime(a)
        : getSortableTime(a) - getSortableTime(b),
    );
  }, [purchaseOrders, sortRecentDesc]);

  const getMaterials = async () => {
    if (!materials.length) {
      setWarehouseLoading(true);
    }
    try {
      const res = await fetch(`${BASE_URL}/org/warehouse/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: organisationId }),
      });
      if (!res.ok) {
        throw new Error("Failed to fetch materials");
      }
      const data = await res.json();
      setMaterials(data);
      localStorage.setItem("materials", JSON.stringify(data));
    } catch (err) {
      console.error("Error fetching materials:", err);
    } finally {
      setWarehouseLoading(false);
    }
  };

  const getPos = async () => {
    if (!purchaseOrders.length) {
      setPoLoading(true);
    }
    try {
      const res = await fetch(
        `${BASE_URL}/inventory/purchase-orders/org/${organisationId}/`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        },
      );
      if (!res.ok) {
        throw new Error("Failed to fetch Purchase Orders");
      }
      const data = await res.json();
      setPurchaseOrders(data.reverse());
      localStorage.setItem("purchaseOrders", JSON.stringify(data));
    } catch (err) {
      console.error("Error fetching Purchase Orders:", err);
    } finally {
      setPoLoading(false);
    }
  };

  useEffect(() => {
    if (organisationId) {
      getMaterials();
      getPos();
    }
  }, [organisationId]);

  if (!organisationId) {
    return <OrganizationEmptyState />;
  }

  if (
    currentMember?.procurement_control === false &&
    currentMember?.joined_as !== "owner" &&
    currentMember?.is_admin === false &&
    permissionsLoaded
  ) {
    return (
      <RequireAccess
        module="Warehouse"
        currentMember={currentMember}
        permission="procurement_control"
      />
    );
  }

  return (
    <div
      className="px-10 pt-8 pb-1 flex flex-col relative"
      style={{ overflowY: "auto" }}
    >
      <LoadingScreen loading={pageLoading}>
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Warehouse</h1>
            <p className="text-sm text-muted-foreground">
              Manage warehouse, materials, and deliveries
            </p>
          </div>
        </div>

        <div className="mb-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-9 p-1">
              {WAREHOUSE_TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.label}
                    className="h-7 text-sm px-4 gap-1.5"
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {tab.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex items-center justify-between mb-3 pr-2">
          <div className="text-sm ml-4 text-gray-600">
            {activeTab === "Warehouse Materials"
              ? `${materials.length} Materials`
              : `${warehousePos.length} Deliveries`}
          </div>
          <div className="flex items-center gap-3">
            <button
              className={`flex items-center gap-1 text-sm text-[#3B0069] border border-gray-200 rounded-full px-3 py-1 bg-[#ECE6F0] hover:bg-[#e0d4e9] transition ${
                activeTab === "Warehouse Purchases" ? "mr-8" : ""
              }`}
              onClick={() => setSortRecentDesc((p) => !p)}
            >
              Sorted by Recent
              <span>
                <ArrowDown
                  size={14}
                  className={`${sortRecentDesc ? "" : "rotate-180"} transition-transform`}
                />
              </span>
            </button>

            {activeTab === "Warehouse Materials" && (
              <Button
                onClick={() => setShowCreateWarehouseMaterial(true)}
                className="flex items-center gap-2 bg-[#3B0069] text-white px-2.5 py-1 rounded-lg text-sm hover:!bg-purple-700 transition-colors duration-200"
              >
                <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                  <FaPlus className="w-3 h-3" />
                </span>
                <span>Add Material</span>
              </Button>
            )}
          </div>
        </div>

        <Card>
          <div className="h-[63vh] px-2 overflow-y-auto">
            {activeTab === "Warehouse Purchases" ? (
              poLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader />
                </div>
              ) : warehousePos.length > 0 ? (
                warehousePos.map((po, idx) => (
                  <WarehousePurchaseRow
                    key={`${po.id}_${idx}`}
                    po={po}
                    onView={setOpenPo}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-10">
                  <img
                    src={NoItemIcon}
                    draggable={false}
                    alt="No Items"
                    className="mb-4"
                    width={220}
                  />
                  <span className="text-gray-500 text-2xl">
                    No Purchase Orders
                  </span>
                  <span className="text-gray-500 text-sm">
                    No purchase orders found for warehouse
                  </span>
                </div>
              )
            ) : sortedWarehouseMaterials.length > 0 ? (
              sortedWarehouseMaterials.map((m, idx) => (
                <WarehouseMaterialRow
                  key={`${idx}`}
                  item={m}
                  onView={() => setSelectedMaterial(m)}
                  onSendReceiveClick={() => setShowSendReceiveSideBar(m)}
                />
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-10">
                <img
                  src={VendorEmptyIcon}
                  draggable={false}
                  alt="No Vendors"
                  className="mb-4"
                  width={240}
                />
                <span className="text-gray-500 text-2xl">
                  No Materials in Warehouse
                </span>
                <span className="text-gray-500 text-sm">
                  Add a material to the warehouse now
                </span>
              </div>
            )}
          </div>
        </Card>

        {showCreateWarehouseMaterial && (
          <CreateWarehouseMaterial
            onClose={() => {
              setShowCreateWarehouseMaterial(false);
              getMaterials();
            }}
            onRedirectToMaterialLibrary={() => {
              setShowCreateWarehouseMaterial(false);
              navigate(
                "/dashboard/settings?section=organization-library&library=material",
              );
            }}
            onSuccess={() => {
              getMaterials();
            }}
          />
        )}
        {selectedMaterial && (
          <WarehouseMaterialSidebar
            material={selectedMaterial}
            onClose={() => setSelectedMaterial(null)}
          />
        )}
        {showSendReceiveSideBar && (
          <SendReceiveSideBar
            material={showSendReceiveSideBar}
            onClose={() => {
              setShowSendReceiveSideBar(false);
              getMaterials();
            }}
          />
        )}
        {openPo && (
          <PurchaseOrder
            rfqId={openPo.rfq_number}
            poCode={openPo.po_number}
            po={openPo}
            vendor={openPo.vendor_name}
            onClose={() => {
              setOpenPo(null);
              getPos();
            }}
            onApproved={() => {
              setActiveTab("Warehouse Purchases");
              getPos();
            }}
          />
        )}
      </LoadingScreen>
    </div>
  );
}
